import { useRef, useState, useEffect, useCallback } from 'react';
import { AlertCircle, Play, RefreshCw } from 'lucide-react';
import { useFaceLandmarker } from '../proctoring/useFaceLandmarker';
import useIsMounted from '../../../../hooks/useIsMounted';

const SWEEP_DURATIONS = [
  { time: 0, prompt: "Face the camera directly" },
  { time: 5, prompt: "Slowly pan the camera left" },
  { time: 10, prompt: "Slowly pan the camera right" },
  { time: 15, prompt: "Show your desk and keyboard" },
  { time: 20, prompt: "Recording complete" }
];
const MAX_RECORDING_TIME = 20;

export default function EnvironmentCameraRecorder({ onCaptureComplete, snapshotCount = 3 }) {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const isMounted = useIsMounted();

  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [currentPrompt, setCurrentPrompt] = useState(SWEEP_DURATIONS[0].prompt);
  const snapshotsRef = useRef([]);
  const { faceLandmarker } = useFaceLandmarker();
  const [warningMessage, setWarningMessage] = useState(null);
  const faceCountRef = useRef(0);
  const animationFrameRef = useRef(null);

  // Initialize camera
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true
      });
      streamRef.current = mediaStream;
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setError(null);
    } catch (err) {
      setError('Camera or microphone access denied. Please allow permissions to proceed.');
      console.error(err);
    }
  };

  useEffect(() => {
    // We wrap startCamera logic slightly to avoid setting state if unmounted,
    // but the main fix is stopping the tracks correctly.
    startCamera();
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const captureSnapshot = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        snapshotsRef.current.push(blob);
      }, 'image/jpeg', 0.8);
    }
  }, []);

  const processVideo = useCallback(() => {
    if (isRecording && videoRef.current && faceLandmarker && videoRef.current.readyState >= 2) {
      const now = performance.now();
      const results = faceLandmarker.detectForVideo(videoRef.current, now);
      faceCountRef.current = results.faceLandmarks ? results.faceLandmarks.length : 0;
    }
    
    if (isRecording) {
      animationFrameRef.current = requestAnimationFrame(processVideo);
    }
  }, [isRecording, faceLandmarker]);

  useEffect(() => {
    if (isRecording) {
      animationFrameRef.current = requestAnimationFrame(processVideo);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isRecording, processVideo]);

  // Timer and Prompt logic during recording
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => {
          // Check face constraints first
          const faces = faceCountRef.current;
          let isValid = true;
          let newWarning = null;
          
          if (prev < 5) {
            if (faces === 0 && faceLandmarker) {
              isValid = false;
              newWarning = "Please face the camera directly.";
            }
          } else {
            if (faces > 0 && faceLandmarker) {
              isValid = false;
              newWarning = "Please point the camera away from your face to show the room.";
            }
          }
          
          if (!isValid) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.pause();
            }
            setWarningMessage(newWarning);
            return prev; // Don't increment timer
          } else {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
              mediaRecorderRef.current.resume();
            }
            setWarningMessage(null);
          }

          const nextTime = prev + 1;

          // Capture snapshots at regular intervals
          if (nextTime % Math.floor(MAX_RECORDING_TIME / snapshotCount) === 0 && nextTime < MAX_RECORDING_TIME) {
            captureSnapshot();
          }

          // Update prompts
          const matchingPrompt = SWEEP_DURATIONS.findLast(s => nextTime >= s.time);
          if (matchingPrompt) {
            setCurrentPrompt(matchingPrompt.prompt);
          }

          if (nextTime >= MAX_RECORDING_TIME) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return nextTime;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording, snapshotCount, captureSnapshot]);

  const startRecording = () => {
    if (!stream) return;

    chunksRef.current = [];
    snapshotsRef.current = [];
    setRecordingTime(0);
    setCurrentPrompt(SWEEP_DURATIONS[0].prompt);

    try {
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm',
        videoBitsPerSecond: 250000 // 250 kbps
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const videoBlob = new Blob(chunksRef.current, { type: 'video/webm' });

        // Capture final snapshot and trigger completion
        if (videoRef.current && canvasRef.current) {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob((blob) => {
            snapshotsRef.current.push(blob);
            onCaptureComplete(videoBlob, snapshotsRef.current);
          }, 'image/jpeg', 0.8);
        } else {
          onCaptureComplete(videoBlob, snapshotsRef.current);
        }
      };

      mediaRecorder.start(200);
      setIsRecording(true);

      captureSnapshot();
    } catch (err) {
      setError('Failed to start recording. MediaRecorder may not be supported on this browser.');
      console.error(err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4 w-full">
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2 w-full">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <div className="relative w-full max-w-2xl bg-black rounded-xl overflow-hidden aspect-video border-2 border-gray-200">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        <canvas ref={canvasRef} className="hidden" />

        {isRecording && (
          <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-between p-6 bg-black/20">
            {warningMessage && (
              <div className="absolute inset-0 bg-red-900/80 flex flex-col items-center justify-center z-50 animate-pulse">
                <AlertCircle className="w-16 h-16 text-white mb-4" />
                <h3 className="text-2xl font-bold text-white text-center max-w-md px-4">{warningMessage}</h3>
                <p className="text-red-100 mt-2">Recording is paused.</p>
              </div>
            )}
            
            <div className="flex justify-between items-center z-10">
              <div className={`flex items-center gap-2 ${warningMessage ? 'bg-orange-500' : 'bg-red-500'} text-white px-3 py-1 rounded-full ${!warningMessage && 'animate-pulse'}`}>
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-sm font-medium">{warningMessage ? 'Paused' : 'Recording'} {recordingTime}s / {MAX_RECORDING_TIME}s</span>
              </div>
            </div>

            <div className="text-center pb-8">
              <h3 className="text-3xl font-bold text-white drop-shadow-lg">
                {currentPrompt}
              </h3>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
              <div
                className="bg-primary h-2 transition-all duration-1000 ease-linear"
                style={{ width: `${(recordingTime / MAX_RECORDING_TIME) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {!isRecording && !error && stream && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <button
              onClick={startRecording}
              className="bg-primary hover:bg-primary/90 text-white p-4 rounded-full shadow-lg transition-transform hover:scale-105 flex items-center gap-2"
            >
              <Play className="w-6 h-6" />
              <span className="font-semibold px-2">Start Environment Sweep</span>
            </button>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-500 text-center max-w-md">
        {!isRecording ?
          "Click start when you're ready. You will be prompted to pan your camera around your room to ensure a secure testing environment." :
          "Please follow the prompts on screen carefully."
        }
      </div>

      {!stream && !error && (
        <button onClick={startCamera} className="text-primary text-sm flex items-center gap-1 hover:underline">
          <RefreshCw className="w-4 h-4" /> Try starting camera again
        </button>
      )}
    </div>
  );
}
